import { useTranslation } from 'react-i18next'

import { useMarketRateApiV1ShipmentsShipmentIdMarketRateGet } from '#/shared/api/generated/shipment/theCargoShipment'
import type {
  ItemDispatch,
  MarketRateItem,
  MarketRateSuccess,
  MarketSummary,
  RoutePoint,
} from '#/shared/api/generated/shipment/models'
import { formatDate } from '#/shared/lib/format'
import { getApiErrorDetail } from '#/shared/lib/api/error-handler'

import { MARKET_RATE_STATUSES } from './types'
import type { MarketRateRow, MarketRateStats, MarketRateStatus } from './types'

interface UseSuggestedPriceArgs {
  shipmentId: string | undefined
  enabled?: boolean
}

interface UseSuggestedPriceResult {
  stats: MarketRateStats | undefined
  rows: MarketRateRow[]
  isLoading: boolean
  /** True whenever a request is in flight, including a manual refetch over stale data. */
  isFetching: boolean
  errorMessage: string | undefined
  refetch: () => void
}

const NOOP = () => {}

const STATUS_BUCKETS: Record<string, MarketRateStatus> = {
  delivered: MARKET_RATE_STATUSES.DELIVERED,
  in_transit: MARKET_RATE_STATUSES.IN_TRANSIT,
  picked_up: MARKET_RATE_STATUSES.PICKED_UP,
  dispatched: MARKET_RATE_STATUSES.DISPATCHED,
  canceled: MARKET_RATE_STATUSES.CANCELED,
  cancelled: MARKET_RATE_STATUSES.CANCELED,
}

const EMPTY: UseSuggestedPriceResult = {
  stats: undefined,
  rows: [],
  isLoading: false,
  isFetching: false,
  errorMessage: undefined,
  refetch: NOOP,
}

function fromSummary(summary: MarketSummary): MarketRateStats {
  return {
    low: summary.low.price,
    lowPerMi: summary.low.per_mile,
    recommended: summary.recommended.price,
    recommendedPerMi: summary.recommended.per_mile,
    high: summary.high.price,
    highPerMi: summary.high.per_mile,
    sampleSize: summary.sample_size,
    referenceDistanceMi: summary.reference_distance_miles,
  }
}

function toStats(success: MarketRateSuccess): MarketRateStats | undefined {
  if (success.summary) return fromSummary(success.summary)
  if (success.sd_prices) {
    const { low, recommended, high } = success.sd_prices
    return { low, recommended, high }
  }
  if (success.cd_prices) {
    return {
      low: success.cd_prices.low,
      recommended: success.estimated_price,
      high: success.cd_prices.high,
    }
  }
  return undefined
}

function formatLocation(point: RoutePoint): string {
  return `${point.city}, ${point.state}`
}

function normalizeStatus(raw: string): MarketRateStatus {
  return STATUS_BUCKETS[raw.toLowerCase()] ?? MARKET_RATE_STATUSES.LISTED
}

function pickDistance(
  item: MarketRateItem,
  dispatch: ItemDispatch | null,
): number | undefined {
  return item.distance_miles ?? dispatch?.distance_miles ?? undefined
}

function dispatchFields(
  dispatch: ItemDispatch | null,
): Pick<
  MarketRateRow,
  | 'status'
  | 'statusLabel'
  | 'dispatchedDate'
  | 'actualPrice'
  | 'actualPricePerMi'
> {
  if (!dispatch) {
    return { status: MARKET_RATE_STATUSES.LISTED }
  }
  const dispatchedAt = dispatch.dispatched_at
  return {
    status: normalizeStatus(dispatch.status),
    statusLabel: dispatch.status_raw ?? undefined,
    dispatchedDate: dispatchedAt ? formatDate(dispatchedAt) : undefined,
    actualPrice: dispatch.price,
    actualPricePerMi: dispatch.per_mile,
  }
}

function toRow(item: MarketRateItem, index: number): MarketRateRow {
  const dispatch = item.dispatch ?? null
  return {
    id: item.id ?? `item-${index}`,
    vehicleType: item.vehicle_type,
    origin: formatLocation(item.route.origin),
    destination: formatLocation(item.route.destination),
    externalUrl: item.external_url ?? undefined,
    distanceMi: pickDistance(item, dispatch),
    listedDate: item.listing.created_at
      ? formatDate(item.listing.created_at)
      : undefined,
    listedPrice: item.listing.price,
    listedPricePerMi: item.listing.per_mile,
    ...dispatchFields(dispatch),
  }
}

function toResult(
  payload: MarketRateSuccess,
): Omit<UseSuggestedPriceResult, 'refetch' | 'isFetching'> {
  return {
    stats: toStats(payload),
    rows: (payload.items ?? []).map(toRow),
    isLoading: false,
    errorMessage: undefined,
  }
}

export function useSuggestedPrice({
  shipmentId,
  enabled = true,
}: UseSuggestedPriceArgs): UseSuggestedPriceResult {
  const { t } = useTranslation()
  const queryEnabled = Boolean(enabled && shipmentId)
  const { data, isFetching, error, refetch } =
    useMarketRateApiV1ShipmentsShipmentIdMarketRateGet(
      shipmentId ?? '',
      undefined,
      {
        query: { enabled: queryEnabled },
      },
    )

  const triggerRefetch = () => {
    void refetch()
  }

  if (!queryEnabled) return { ...EMPTY, refetch: triggerRefetch }

  if (error) {
    return {
      ...EMPTY,
      isFetching,
      refetch: triggerRefetch,
      errorMessage:
        getApiErrorDetail(error) ?? t('table.marketRate.fetchError'),
    }
  }

  const payload = data?.data
  if (!payload)
    return {
      ...EMPTY,
      isFetching,
      refetch: triggerRefetch,
      isLoading: isFetching,
    }
  if (!payload.success)
    return {
      ...EMPTY,
      isFetching,
      refetch: triggerRefetch,
      errorMessage: payload.error,
    }
  return { ...toResult(payload), isFetching, refetch: triggerRefetch }
}
